import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Checkbox, EmptyState, LabelWithHint, Stack, useToast } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import { loxleyApi, type ConnectorKey, type LoxleyAdmin, type LoxleySeat } from "../lib/loxleyApi";
import { useLocale } from "../shell/LocaleProvider";
import { useOptionalLoxley } from "./LoxleyProvider";

/**
 * Organization settings, Loxley tab (org admins). Seats come from the paid add-on
 * Saleslox staff switch on; the admin decides who holds them and whether external
 * AI apps (MCP) may connect. Admins revoke keys but never create one for someone else.
 */
function count(value: number | undefined): string {
  return Number(value || 0).toLocaleString("en-US");
}

export function SettingsLoxleyPanel({ orgId }: { orgId: string }) {
  const toast = useToast();
  const loxley = useOptionalLoxley();
  const { formatDateTime } = useLocale();
  const [data, setData] = useState<LoxleyAdmin | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    loxleyApi.admin(orgId).then((value) => { setData(value); setFailed(false); }).catch(() => setFailed(true));
  }, [orgId]);

  useEffect(load, [load]);

  function fail(error: unknown, fallback: string) {
    toast.show(error instanceof ApiError ? error.message : fallback, "danger");
  }

  async function toggleSeat(seat: LoxleySeat) {
    setBusy(seat.user_id);
    try {
      const next = seat.has_seat
        ? await loxleyApi.removeSeat(orgId, seat.user_id)
        : await loxleyApi.assignSeat(orgId, seat.user_id);
      setData((current) => (current ? { ...current, seats: next.seats, settings: next.settings } : current));
      loxley?.refresh();
      if (seat.has_seat) load();
    } catch (error) {
      fail(error, "The seat could not be changed.");
    } finally {
      setBusy(null);
    }
  }

  async function setConnectors(enabled: boolean) {
    setBusy("connectors");
    try {
      const settings = await loxleyApi.setConnectors(orgId, enabled);
      setData((current) => (current ? { ...current, settings } : current));
      if (!enabled) load();
    } catch (error) {
      fail(error, "The setting could not be saved.");
    } finally {
      setBusy(null);
    }
  }

  async function revoke(key: ConnectorKey) {
    setBusy(key.id);
    try {
      await loxleyApi.adminRevokeKey(orgId, key.id);
      load();
    } catch (error) {
      fail(error, "The key could not be revoked.");
    } finally {
      setBusy(null);
    }
  }

  if (failed) {
    return <EmptyState title="Loxley settings could not be loaded" description="Reload the page to try again." />;
  }
  if (!data) return null;
  if (!data.settings.enabled) {
    return (
      <EmptyState
        title="Loxley is not on your plan yet"
        description="Loxley, the AI sales coach, is a paid add-on priced per seat. Contact Saleslox support to add it."
      />
    );
  }
  const { settings } = data;
  const full = settings.seats_assigned >= settings.seats;

  return (
    <Stack gap={4}>
      <Card>
        <Stack gap={3}>
          <h2 className="home-section-title">Seats</h2>
          <p className="home-subtitle">
            {settings.seats_assigned} of {settings.seats} seats in use. Each person with a seat can ask Loxley
            and connect their own AI apps. In-app requests use the organization&rsquo;s monthly AI limit.
            This month: {data.usage.app_turns} requests in the app, {data.usage.mcp_tool_calls} calls from AI apps.
            Tokens are what each person&rsquo;s in-app requests used on the AI connection. Connected AI apps
            use their own model and no Saleslox tokens.
          </p>
          <table className="loxley-table">
            <thead>
              <tr>
                <th>Person</th>
                <th className="loxley-num">
                  <LabelWithHint label="Requests" tooltip="In-app Loxley requests this person made this month. Calls from Claude or ChatGPT are not included." />
                </th>
                <th className="loxley-num">
                  <LabelWithHint label="Input tokens" tooltip="Tokens sent to the model for this person's in-app requests this month. A connected Claude or ChatGPT app uses its own model and is not counted here." />
                </th>
                <th className="loxley-num">
                  <LabelWithHint label="Output tokens" tooltip="Tokens the model sent back for this person's in-app requests this month." />
                </th>
                <th>
                  <LabelWithHint label="Seat" tooltip="A seat lets this person ask Loxley in the app and open Connected AI apps on their account. Without a seat, that card is hidden." />
                </th>
              </tr>
            </thead>
            <tbody>
              {data.seats.map((seat) => (
                <tr key={seat.user_id}>
                  <td>{seat.display_name ? `${seat.display_name} (${seat.email})` : seat.email}</td>
                  <td className="loxley-num">{count(seat.usage?.requests)}</td>
                  <td className="loxley-num">{count(seat.usage?.input_tokens)}</td>
                  <td className="loxley-num">{count(seat.usage?.output_tokens)}</td>
                  <td>
                    <Checkbox
                      label={seat.has_seat ? "Has a seat" : full ? "No seats left" : "Give a seat"}
                      checked={seat.has_seat}
                      disabled={busy === seat.user_id || (!seat.has_seat && full)}
                      onChange={() => void toggleSeat(seat)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      </Card>
      <Card>
        <Stack gap={3}>
          <h2 className="home-section-title">External AI apps (MCP)</h2>
          {!data.mcp_global ? (
            <p role="status">Saleslox has external AI apps switched off for everyone at the moment.</p>
          ) : null}
          <Checkbox
            label="Allow people with a seat to connect Claude, ChatGPT and other AI apps"
            tooltip="Each person with a seat connects as themselves. There is no organisation-wide key. Turning this off disconnects every app and revokes every key."
            checked={settings.connectors_enabled}
            disabled={busy === "connectors"}
            onChange={(event) => void setConnectors(event.target.checked)}
          />
          <p className="home-subtitle">
            <Link to="/help?guide=org-admin&topic=connect-ai-apps">How to connect Claude or ChatGPT</Link>
          </p>
          <p className="home-subtitle">
            Turning this off disconnects every AI app and revokes every key in your organization at once.
          </p>
          {data.keys.length ? (
            <table className="loxley-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Name</th>
                  <th>
                    <LabelWithHint
                      label="Access"
                      tooltip="Read only lets the app look up records and cannot propose changes. Read and propose lets it prepare changes. The person still confirms each change in Saleslox. Deletes, money, and anything sent to a customer can only be approved in Saleslox."
                    />
                  </th>
                  <th>
                    <LabelWithHint label="Last used" tooltip="When this app or key last called Saleslox. Never means it has not been used yet." />
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.keys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.email}</td>
                    <td>{key.kind === "api_key" ? `${key.name} (${key.prefix}…)` : `${key.name} (signed in)`}</td>
                    <td>{key.scope === "read" ? "Read only" : "Read and propose"}</td>
                    <td>{key.last_used_at ? formatDateTime(key.last_used_at) : "Never"}</td>
                    <td>
                      <Button size="sm" variant="danger" disabled={busy === key.id} onClick={() => void revoke(key)}>
                        Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="home-subtitle">No AI apps are connected.</p>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
